import { FrameName } from "@/game/entities/config";
import { EventCategory } from "@/types/ui";
import { isBot } from "@/utils/bot";

export const GAME_TICKER_INTERVAL = 10_000;
export const GAME_TICKER_ENABLED = true;
export const TICK_DURATION = GAME_TICKER_INTERVAL / 1_000; // sec

export const SEGMENT_BORDER_ENABLED = false;

export const ENV = import.meta.env.VITE_ENV;
export const IS_DEV = ENV === "dev";
export const IS_QA = ENV === "qa";
export const IS_UAT = ENV === "uat";
export const IS_PROD = ENV === "production";
export const IS_BOT = isBot();
export const IMAGES_CDN_URL = import.meta.env.VITE_PATH_IMAGES;

export const ENABLE_SIEGE_SYSTEM_TOASTS = IS_DEV || IS_QA;

export const CONCURRENCY_LIMIT = Math.max(
	Math.trunc(navigator.hardwareConcurrency / 2),
	1,
);

export const MAP_ACTION_BATCH_SIZE = 1_000; // teoreticky by to zvladlo aj 3k, ak sa nastavi na nulu, tak sa odosle vsetko naraz

const params = new URLSearchParams(window.location.search);
export const PLATFORM = params.get("platform") || "";
export const FLAG_ENABLE_TIMERS =
	params.has("enable-timers") || !!localStorage.getItem("enable-timers");
export const FLAG_REVEAL_MAP =
	params.has("reveal-map") || !!localStorage.getItem("reveal-map");
export const FLAG_DISABLE_TUTORIAL =
	params.has("disable-tutorial") ||
	!!localStorage.getItem("disable-tutorial");
export const FLAG_DISABLE_TOOLTIPS =
	params.has("disable-tooltips") ||
	!!localStorage.getItem("disable-tooltips");
export const FLAG_ENABLE_WEB =
	params.has("enable-web") || !!localStorage.getItem("enable-web");
export const FLAG_OFFLINE_TIME =
	(params.has("offline-time") && +params.get("offline-time")!) ||
	+(localStorage.getItem("offline-time") ?? 0);
if (params.has("offline-time")) params.delete("offline-time");

if ((IS_DEV || IS_QA) && params.has("deviceSecret")) {
	localStorage.setItem("deviceSecret", params.get("deviceSecret")!);
}

export const CLICK_ANIMATION_DEPTH_CORRECTION = 1_000;
export const PROGRESS_BAR_DEPTH_CORRECTION = 1_001;
export const SELECTOR_DEPTH_CORRECTION = 1_002;
export const TOAST_DEPTH_CORRECTION = 2_000;
export const TILE_LABEL_CORRECTION = 1_100;
export const EXPLORE_ANIMATION_DEPTH_CORRECTION = 1_101;

export const OFFLINE_INCOME_DEFAULT_MULTIPLIER = 1;
export const OFFLINE_INCOME_WITH_AD_MULTIPLIER = 1.5;
export const ZOOM_NO_TILE_DETAIL = 0.125;

export const OFFER_BUILDING_UNLOCK_LEVEL = Number.MAX_SAFE_INTEGER;
export const EVENT_BUILDING_UNLOCK_LEVEL = Number.MAX_SAFE_INTEGER - 1;
export const ENEMY_SALVAGEABLE_UNLOCK_LEVEL = Number.MAX_SAFE_INTEGER - 100; // Temporary constant.

export const SAFE_COORDINATE_OUTSIDE_MAP = Math.trunc(
	Number.MAX_SAFE_INTEGER / 2,
);

export const NUMBER_ANIMATION_DURATION = 750; // ms
export const CLICK_ANIMATION_DURATION = 500; // ms
export const FADE_ANIMATION_DURATION = 500; // ms

export const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
export const BOOST_DURATION = 5; // 5 minutes
export const BOOST_TICK_DURATION = (5 * 60) / 10; // 30 ticks
export const INCOME_BOOST_COEFFICIENT = 3;
export const BOOST_MAX = 5; //ks
export const AD_COMPENSATION_TICK_COUNT = 3;

export const SPECIAL_BUILDING_PACK_COOLDOWN = 30 * 60 * 1_000; // 30 minutes
export const SPECIAL_BUILDING_PACK_DURATION = 60 * 60 * 1_000; // 60 minutes

export const premiumBuildingsPrices = {
	[FrameName.ADVANCED_IRON_MINE]: 100,
	[FrameName.INDUSTRIAL_LUMBER_MILL]: 50,
	[FrameName.LARGE_CLAY_PIT]: 100,
	[FrameName.LARGE_STONE_QUARRY]: 75,
	[FrameName.OXEN_STABLE]: 150,
	[FrameName.PATISSERIE]: 100,
	[FrameName.PREMIUM_GOLD_MINE]: 100,
	[FrameName.UNIVERSAL_WAREHOUSE]: 200,
	[FrameName.CARPENTRY_DISTRICT]: 100,
	[FrameName.STONEMASONRY_DISTRICT]: 150,
	[FrameName.TAX_OFFICE]: 250,
	[FrameName.INDUSTRIAL_FOUNDRY]: 150,
	[FrameName.BRICKYARD]: 200,
	[FrameName.BATH_HOUSE]: 300,
	[FrameName.TEMPLE]: 400,
	[FrameName.SUPPLY_CARAVAN]: 150,
	[FrameName.RANGERS]: 50,
	[FrameName.COMMAND_POST]: 200,
	[FrameName.MILITARY_ACADEMY]: 500,
};
export const UNSUPPORTED_BITMAP_LANGUAGES = [
	"zh",
	"ko",
	"ja",
	"tc",
	"el",
	"ru",
	"uk",
	"bg",
	"sr",
];

export const SUPER_CLICKER_TOOLTIP_THRESHOLD = 5;

export const LAST_SEEN_EVENT_ID_KEY = "last_seen_event_id";
export const LAST_SEEN_EVENT_INTRO_ID_KEY = "last_seen_event_intro_id";
export const LAST_SEEN_EVENT_UPGRADES_ID_KEY = "last_seen_event_upgrades_id";
export const LAST_SEEN_EVENT_SHOP_ID_KEY = "last_seen_event_shop_id";

// easter event
export const EASTER_PICKUP_CAP = 10;
export const EASTER_EVENTIUM_PER_PICKUP = 2;
export const EASTER_EVENTIUM_PER_RARE_PICKUP = 50;
export const EASTER_RARE_PICKUP_CHANCE = 0.01;
export const EASTER_MAX_SPAWN_DISTANCE = 200;

// halloween event
export const HALLOWEEN_PICKUP_CAP = 10;
export const HALLOWEEN_EVENTIUM_PER_PICKUP = 10;
export const HALLOWEEN_EVENTIUM_PER_RARE_PICKUP = 300;
export const HALLOWEEN_SPAWN_CHANCE = 0.25;
export const HALLOWEEN_RARE_PICKUP_CHANCE = 0.01;
export const HALLOWEEN_STARTING_PICKUP_COUNT = 5;
export const HALLOWEEN_OFFLINE_PICKUP_INTERVAL_SECONDS = 60;
export const HALLOWEEN_MAX_SPAWN_DISTANCE = 200;

// pirates event
export const PIRATES_PICKUP_CAP = 10;
export const PIRATES_EVENTIUM_PER_PICKUP = 10;
export const PIRATES_EVENTIUM_PER_RARE_PICKUP = 300;
export const PIRATES_SPAWN_CHANCE = 0.25;
export const PIRATES_RARE_PICKUP_CHANCE = 0.01;
export const PIRATES_STARTING_PICKUP_COUNT = 5;
export const PIRATES_OFFLINE_PICKUP_INTERVAL_SECONDS = 60;
export const PIRATES_MAX_SPAWN_DISTANCE = 200; //TODO Pirates

// firefest event
export const FIREFEST_BUILDINGS_DEFAULT_LIMIT = 5;

// solar event
export const SOLAR_BUILDINGS_DEFAULT_LIMIT = 5;

// PIGI BANK
export const PIGGY_BANK_QUEST_ID = 10001;
export const PIGGY_BANK_MAX_PROGRESS = 6600;
export const PIGGY_BANK_OFFER_ID = "lok_piggy_bank";
export const PIGGY_BANK_PROGRESS_PER_CLICK = 10;

// plague
export const PLAGUE_ENABLED = true;
export const PLAGUE_HP_MAX = 20;
export const PLAGUE_MAX_DAMAGE_DEFAULT = 3;

export const EVENTS_WITH_MILESTONES = [
	EventCategory.CHRISTMAS,
	EventCategory.EASTER,
	EventCategory.FIREFEST,
	EventCategory.FISHING,
	EventCategory.HUNTING,
	EventCategory.HALLOWEEN,
];
// PROVISION offers
export const ROYAL_PROVISION_OFFER_IDS = ["lok_provision_1", "lok_provision_2"];
